// StatusSummaryCards.tsx

import { Grid2, Box, Typography, TextField, Button, Chip, FormControl } from '@mui/material';
import { useCommonStyles } from '../../styles/GenericStyle';
import { red, white } from '../../colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { useState, useEffect } from "react";
import IconButton from '@mui/material/IconButton';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { GenericConstant } from '../../utils/GenericConstant';
import { toTitleCase } from '../../utils/HelperFunction';
import { useUpdateComment } from '../../api/vegApl/Technician';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGlobalState } from "../../contexts/GlobalState";

const normalizeStepName = (stepName?: string) => String(stepName ?? '').trim().toLowerCase();

export const isScannedTreatmentStep = (stepName?: string) => {
    const normalized = normalizeStepName(stepName);
    if (normalized.includes('ferment')) return true;
    return /disinfection[^0-9]*([1-3])\b/.test(normalized);
};

export const renderProtocolInstruction = (instruction: string, stepName?: string) => {
    const chunks = instruction.split(',');
    const shouldUseScannedTreatment = isScannedTreatmentStep(stepName);

    return chunks.map((chunk, index) => {
        const colonIndex = chunk.indexOf(':');
        const commaPrefix = index > 0 ? ', ' : '';

        if (colonIndex === -1) {
            const plainText = chunk.trim();
            const displayText = shouldUseScannedTreatment
                ? plainText.replace(/\bresource\s*id\b/gi, 'Scanned Treatment')
                : plainText;
            return <span key={`chunk-${index}`}>{`${commaPrefix}${displayText}`}</span>;
        }

        const label = chunk.slice(0, colonIndex).trim();
        const value = chunk.slice(colonIndex + 1).trim();
        const displayLabel = shouldUseScannedTreatment && label.toLowerCase() === 'resource id'
            ? 'Scanned Treatment'
            : label;

        return (
            <span key={`chunk-${index}`}>
                {commaPrefix}
                <Box component="span" sx={{ fontWeight: 700 }}>
                    {displayLabel}:
                </Box>
                {value ? ` ${value}` : ''}
            </span>
        );
    });
};

export type Step = {
    isShowCommentBox?: boolean,
    protocol_step_id: number,
    protocol_step_instruction: string,
    protocol_step_name: string,
    workflow_step_comment: string,
    workflow_step_status: string
}
export type steeperProps = {
    stepsData: Step[],
    drillDownParam: any,
    SPLotWorkflowSteps: any,
    setIsRefreshDrillDown: (val: boolean) => void,
}
export default function SpLotProcessingStage({ stepsData, drillDownParam, SPLotWorkflowSteps, setIsRefreshDrillDown }: steeperProps) {
    const classes = useCommonStyles();
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [stepsIns, setStepsIns] = useState<Step[]>([]);
    const { showSnackbar } = useSnackbar();
    const { state: globalState } = useGlobalState();    
    const { mutate: updateComment } = useUpdateComment({
        mutation: {
            onSuccess: () => {
                showSnackbar({ message: "Comment updated successfully", severity: 'success' });
                setIsRefreshDrillDown(true);
            },
            onError: () => {
                showSnackbar({ message: "error in updating Comment", severity: 'error' });
            },
        },
    })

    useEffect(() => {
        const initialData: Record<string, any> = {};
        const commentError: Record<string, any> = {};
        stepsData?.forEach((field: any) => {
            initialData["txt_comment_" + field.protocol_step_id] = field?.workflow_step_comment;
            field.isShowCommentBox = field?.workflow_step_comment ? false : true;
            commentError["txt_comment_error_" + field.protocol_step_id] = false;
        });
        console.log("stepsData", stepsData);
        setFormData(initialData);
        setStepsIns(stepsData);      

    }, [stepsData])


    const changeComment = (txtVal: string, workFlowId: number) => {
        const key = `txt_comment_${workFlowId}`;       
        setFormData(prev => ({
            ...prev,
            [key]: txtVal,
        }));
    };

    const updateAddCommentBox = (ind: number) => {
        setStepsIns(prev =>
            prev.map((step, i) =>
                i === ind ? { ...step, isShowCommentBox: true } : step
            )
        );
    }

    const updateComAction = (workFlowId: number) => {
        if (formData['txt_comment_' + workFlowId]) {
            let req: any = {
                "comment": formData['txt_comment_' + workFlowId] ?? "",
                "splot_ids": [drillDownParam?.splot_id],
                "user_id": globalState.user_id_db,
                "workflow_step_id": workFlowId,
                "workflow_protocol_id": SPLotWorkflowSteps?.current_workflow_protocol_id
            }
            updateComment({ data: req });
        }
    }

    const getEquipmentDisplayValue = (step: any): string => {
        const isScanned = isScannedTreatmentStep(step?.protocol_step_name);
        
        if (isScanned) {
            return String(step?.splot_step_treatment_name ?? '').trim();
        }
        
        const equipmentId = String(step?.workflow_step_equipment_id ?? step?.workflow_step_resource_id ?? '').trim();
        if (!equipmentId) return equipmentId;

        const stepEquipments = Array.isArray(step?.equipments) ? step.equipments : [];
        const matchedEquipment = stepEquipments.find((equipment: any) =>
            String(equipment?.equipment_id ?? '').trim().toLowerCase() === equipmentId.toLowerCase()
            && String(equipment?.display_name ?? '').trim() !== ''
        );

        return String(matchedEquipment?.display_name ?? equipmentId);
    }

    return (
        <Grid2 container spacing={1} wrap="wrap" sx={{ width: '100%', mt: "20px" }}>
            <Box display={"flex"} flexDirection={"column"} sx={{ width: "100%" }}>
                {stepsIns?.map((steps: any, index) => (
                    <Box display={"flex"} flexDirection={"column"} sx={{ 'border': '1px solid #939393', width: "100%",p:2, mt: index==0 ? '0px' : "20px",borderRadius:"4px" }}>
                        <Box display={"flex"} alignItems={"center"} flexDirection={"row"}  sx={{mt:"10px"}}>
                            <Typography component="span" className={classes.labelNoto16Bold500} >
                                {steps.protocol_step_name ?? ""}
                            </Typography>
                            {steps?.workflow_step_status && <Chip
                                label={toTitleCase(steps?.workflow_step_status ?? "")}
                                sx={{
                                    color: GenericConstant.CHIPCOLOR[toTitleCase(steps?.workflow_step_status) + "Color"],
                                    bgcolor: GenericConstant.CHIPCOLOR[toTitleCase(steps?.workflow_step_status) + "BgColor"],
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    ml:"6px",
                                    "& .MuiChip-label": {
                                        fontSize: 12,
                                        fontWeight: 400,
                                    }

                                }}
                            />}
                            <Typography component="span" sx={{ml:"12px"}}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto12Bold400} >
                                    {isScannedTreatmentStep(steps?.protocol_step_name) ? 'Scanned Treatment:' : 'Equipment ID:'}
                                </Typography>
                                <Typography component="span" className={classes.labelNoto12Bold500} sx={{ ml: "4px" }}>
                                    {getEquipmentDisplayValue(steps)}

                                </Typography>
                            </Typography>
                        </Box>
                        {steps?.protocol_step_instruction && <Box width={"100%"} sx={{ backgroundColor: "#FFFCDD", p: 1, border: "1px solid #CCB25B", mt: "12px" }}>
                            <Typography component="span" className={classes.labelNoto14Bold400}>
                                <InfoOutlinedIcon sx={{ verticalAlign: 'middle', fontSize: 16 }} /> {renderProtocolInstruction(steps?.protocol_step_instruction ?? "", steps?.protocol_step_name)}
                            </Typography>
                        </Box>}

                        {!steps.isShowCommentBox && <Box width={"100%"} sx={{ backgroundColor: steps?.workflow_step_status === GenericConstant.WORKFLOWSKIP ? white : "#EFF6FF", p: 1, mt: 1, border: "1px solid #BDBDBD", borderRadius: 1 }} >
                            <Typography component="div" sx={{ display: "flex" }} className={classes.labelNoto12Bold400}>
                                <Box display={"flex"} flexDirection={"column"} width={`calc(100% - 34px)`}>
                                    <Box width={"100%"} sx={{ mt: 1, border: `1px solid  ${steps?.workflow_step_status === GenericConstant.WORKFLOWSKIP} ?  #BDBDBD : #1447E6` }} >
                                        <Typography component="div" width={"100%"}  sx={{ color: steps?.workflow_step_status === GenericConstant.WORKFLOWSKIP ? red : "#364153",wordWrap:"break-word" }} className={classes.labelNoto14Bold400}>
                                            <ChatBubbleOutlineOutlinedIcon sx={{ verticalAlign: 'middle', fontSize: 16, mr: 1 }} />{steps?.workflow_step_comment ?? ""}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box flex={"display"} gap={0} sx={{ display:"flex",justifyContent:"flex-end"}}>
                                    <IconButton disableFocusRipple disableRipple sx={{
                                        "&:focus": { outline: "none" },
                                        "&.Mui-focusVisible, &:focus-visible": {
                                            outline: "none",
                                            boxShadow: "none",
                                        },
                                    }} onClick={() => { updateAddCommentBox(index) }}>
                                       
                                        <img src={"icons/Edit.svg"} width={18} height={18} />
                                    </IconButton>
                                    
                                </Box>
                            </Typography>
                        </Box>}
                        {steps.isShowCommentBox && <Box width={"100%"} sx={{mt: "8px" }} >
                            <Typography component="div" sx={{ color: "#1447E6" }} className={classes.labelNoto12Bold400}>
                                Add Comment
                            </Typography>
                            <Box display={"flex"}>
                                <FormControl size="small" fullWidth>
                                    <TextField variant="outlined"
                                        placeholder='Enter comments'
                                        value={formData[`txt_comment_${steps?.protocol_step_id}`] ?? ""}
                                        onChange={(e) => changeComment(e.target.value, steps?.protocol_step_id)}
                                        fullWidth
                                        sx={(theme) => ({
                                            mt: 1,
                                            ...MtComStyleObj(theme).textFocusHoverStyle,
                                             ...MtComStyleObj(theme).textBoxValueStyle, 
                                        })}
                                        slotProps={{
                                            input: {
                                                sx: { height: 40, backgroundColor: "white", mr: 25 },
                                            }
                                        }} />
                                </FormControl>

                                <Button
                                    variant="contained" disableRipple
                                    className={classes.greenButton}
                                    onClick={() => updateComAction(steps?.protocol_step_id)}  
                                    disabled={!formData['txt_comment_' + steps?.protocol_step_id]}                                
                                    startIcon={
                                        <img
                                            src={"icons/Save.svg"}
                                            alt="Save"
                                            width={20}
                                            height={20}
                                            style={{ display: "block" }}
                                        />
                                    }
                                    sx={(theme) => ({
                                        ...MtComStyleObj(theme).saveCommentStepsStyle,
                                        ...MtComStyleObj(theme).disabledGreenBtnStyle,
                                        ml: 1, mt: 1
                                    })}
                                />
                            </Box>
                        </Box>}
                     
                    </Box>
                ))}
            </Box>
        </Grid2>
    );
}